
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type {
  RecruitmentState,
  JobFilters,
  ApplicationFilters,
  Job,
  ApplicationDetail,
  ActiveCompany,
} from "./types";
import {
  fetchJobs,
  fetchJobDetails,
  deleteJob,
  fetchApplications,
  fetchApplicationDetails,
  fetchInterviews,
  fetchPendingFeedbacks,
  fetchMyReferrals,
  fetchHRReferrals,
  fetchReferralStatistics,
  fetchTopReferrers,
  fetchReferralRewards,
  updateReferralRewardStatus,
  fetchReferralRewardSummary,
  fetchCareerPageSettings,
  fetchCareerPageLink,
  createCareerPageLinkThunk,
  fetchIntegrations,
  fetchATSScoringWeights,
  toggleCareerPageLinkThunk,
  fetchRoundTemplates,
  createRoundTemplate,
  updateRoundTemplate,
  deleteRoundTemplate,
  fetchJobRounds,
  saveJobRounds,
  fetchAvailableInterviewers,
  fetchApplicationRounds,
  updateApplicationRoundResult,
} from "./thunks";

const initialJobFilters: JobFilters = {
  search: "",
  department_id: null,
  employment_type: null,
  status: null,
  page: 1,
  limit: 10,
};

const initialApplicationFilters: ApplicationFilters = {
  search: "",
  job_id: null,
  status: null,
  source: null,
  priority: null,
  assigned_to: null,
  min_score: null,
  max_score: null,
  date_from: null,
  date_to: null,
  sort_by: "applied_at",
  sort_order: "DESC",
  page: 1,
  limit: 10,
};

const DEFAULT_COMPANY: ActiveCompany = {
  company_id: "",
  company_name: "Select Company",
  company_type: "Type",
};

const initialState: RecruitmentState = {
  activeCompany: DEFAULT_COMPANY,

  
  jobs: [],
  jobsPagination: null,
  jobsLoading: false,
  activeJob: null,
  activeJobLoading: false,

  
  applications: [],
  applicationsPagination: null,
  applicationsLoading: false,
  activeApplication: null,
  activeApplicationLoading: false,

  
  interviews: [],
  interviewsLoading: false,
  pendingFeedbacks: [],

  roundTemplates: [],
  roundTemplatesLoading: false,
  jobRounds: [],
  jobRoundsLoading: false,
  availableInterviewers: [],
  availableInterviewersLoading: false,

  
  referrals: [],
  referralsLoading: false,
  referralStats: null,
  topReferrers: [],
  referralRewards: [],
  referralRewardsLoading: false,
  referralRewardSummary: [],

  
  careerPageSettings: null,
  careerPageSettingsLoading: false,
  careerPageLink: null,
  careerPageLinkLoading: false,
  integrations: [],
  integrationsLoading: false,
  atsScoringWeights: null,
  atsScoringWeightsLoading: false,

  applicationRounds: [],
  applicationRoundsLoading: false,

  
  jobFilters: initialJobFilters,
  applicationFilters: initialApplicationFilters,

  
  error: null,
};

const recruitmentSlice = createSlice({
  name: "recruitment",
  initialState,
  reducers: {
    setActiveCompany: (state, action: PayloadAction<ActiveCompany>) => {
      state.activeCompany = action.payload;
    },
    setJobFilters: (state, action: PayloadAction<Partial<JobFilters>>) => {
      state.jobFilters = { ...state.jobFilters, ...action.payload };
    },
    resetJobFilters: (state) => {
      state.jobFilters = initialJobFilters;
    },
    setApplicationFilters: (
      state,
      action: PayloadAction<Partial<ApplicationFilters>>,
    ) => {
      state.applicationFilters = {
        ...state.applicationFilters,
        ...action.payload,
      };
    },
    resetApplicationFilters: (state) => {
      state.applicationFilters = initialApplicationFilters;
    },
    setActiveJob: (state, action: PayloadAction<Job | null>) => {
      state.activeJob = action.payload;
    },
    setActiveApplication: (
      state,
      action: PayloadAction<ApplicationDetail | null>,
    ) => {
      state.activeApplication = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      
      .addCase(fetchJobs.pending, (state) => {
        state.jobsLoading = true;
        state.error = null;
      })
      .addCase(fetchJobs.fulfilled, (state, action) => {
        state.jobsLoading = false;
        state.jobs = Array.isArray(action.payload?.jobs) ? action.payload.jobs : (Array.isArray(action.payload) ? action.payload : []);
        state.jobsPagination = action.payload?.pagination || null;
      })
      .addCase(fetchJobs.rejected, (state, action) => {
        state.jobsLoading = false;
        state.error = action.payload as string;
      })

      .addCase(fetchJobDetails.pending, (state) => {
        state.activeJobLoading = true;
      })
      .addCase(fetchJobDetails.fulfilled, (state, action) => {
        state.activeJobLoading = false;
        state.activeJob = action.payload;
      })
      .addCase(fetchJobDetails.rejected, (state, action) => {
        state.activeJobLoading = false;
        state.error = action.payload as string;
      })

      .addCase(deleteJob.fulfilled, (state, action) => {
        state.jobs = state.jobs.filter((j) => j.id !== action.payload);
      })

      
      .addCase(fetchApplications.pending, (state) => {
        state.applicationsLoading = true;
        state.error = null;
      })
      .addCase(fetchApplications.fulfilled, (state, action) => {
        state.applicationsLoading = false;
        const raw: any[] = Array.isArray(action.payload?.applications) ? action.payload.applications : (Array.isArray(action.payload) ? action.payload : []);
        
        state.applications = raw.map((a: any) => ({
          ...a,
          id: a.id ?? a.application_id,
          application_id: a.application_id ?? a.id,
        }));
        state.applicationsPagination = action.payload?.pagination || null;
      })
      .addCase(fetchApplications.rejected, (state, action) => {
        state.applicationsLoading = false;
        state.error = action.payload as string;
      })

      .addCase(fetchApplicationDetails.pending, (state) => {
        state.activeApplicationLoading = true;
      })
      .addCase(fetchApplicationDetails.fulfilled, (state, action) => {
        state.activeApplicationLoading = false;
        const payload = action.payload || {};
        state.activeApplication = {
          ...payload,
          id: payload.id ?? payload.application_id,
          application_id: payload.application_id ?? payload.id,
        };
      })
      .addCase(fetchApplicationDetails.rejected, (state, action) => {
        state.activeApplicationLoading = false;
        state.error = action.payload as string;
      })

      
      .addCase(fetchInterviews.pending, (state) => {
        state.interviewsLoading = true;
      })
      .addCase(fetchInterviews.fulfilled, (state, action) => {
        state.interviewsLoading = false;
        const raw: any[] = Array.isArray(action.payload?.interviews) ? action.payload.interviews : (Array.isArray(action.payload) ? action.payload : []);
        
        state.interviews = raw.map((item: any) => ({
          ...item,
          id: item.id ?? item.interview_id,
          candidate_name: item.candidate_name ?? item.candidate?.full_name ?? "",
          job_title: item.job_title ?? item.job?.job_title ?? "",
          interviewer_names: (item.interviewers || []).map((i: any) => i.name),
          meeting_link: item.meeting_link ?? null,
          feedback_submitted: item.feedback_submitted ?? false,
        }));
      })
      .addCase(fetchInterviews.rejected, (state, action) => {
        state.interviewsLoading = false;
        state.error = action.payload as string;
      })

      .addCase(fetchPendingFeedbacks.fulfilled, (state, action) => {
        const raw: any[] = Array.isArray(action.payload) ? action.payload : [];
        state.pendingFeedbacks = raw.map((item: any) => ({
          ...item,
          id: item.id ?? item.interview_id,
          candidate_name: item.candidate_name ?? item.candidate?.full_name ?? "",
          job_title: item.job_title ?? item.job?.job_title ?? "",
          interviewer_names: (item.interviewers || []).map((i: any) => i.name),
          meeting_link: item.meeting_link ?? null,
          feedback_submitted: item.feedback_submitted ?? false,
        }));
      })

      .addCase(fetchRoundTemplates.pending, (state) => {
        state.roundTemplatesLoading = true;
      })
      .addCase(fetchRoundTemplates.fulfilled, (state, action) => {
        state.roundTemplatesLoading = false;
        state.roundTemplates = action.payload?.templates || [];
      })
      .addCase(fetchRoundTemplates.rejected, (state, action) => {
        state.roundTemplatesLoading = false;
        state.error = action.payload as string;
      })

      .addCase(fetchJobRounds.pending, (state) => {
        state.jobRoundsLoading = true;
      })
      .addCase(fetchJobRounds.fulfilled, (state, action) => {
        state.jobRoundsLoading = false;
        state.jobRounds = action.payload?.rounds || [];
      })
      .addCase(fetchJobRounds.rejected, (state, action) => {
        state.jobRoundsLoading = false;
        state.error = action.payload as string;
      })

      .addCase(saveJobRounds.fulfilled, (state, action) => {
        state.jobRounds = action.payload?.rounds || [];
      })

      .addCase(fetchAvailableInterviewers.pending, (state) => {
        state.availableInterviewersLoading = true;
      })
      .addCase(fetchAvailableInterviewers.fulfilled, (state, action) => {
        state.availableInterviewersLoading = false;
        const data = action.payload;
        // Combine available + busy into one list for the dropdown
        const available = Array.isArray(data?.available_interviewers)
          ? data.available_interviewers
          : [];
        const busy = Array.isArray(data?.busy_interviewers)
          ? data.busy_interviewers
          : [];
        state.availableInterviewers = [...available, ...busy];
      })
      .addCase(fetchAvailableInterviewers.rejected, (state) => {
        state.availableInterviewersLoading = false;
      })

      
      .addCase(fetchMyReferrals.pending, (state) => {
        state.referralsLoading = true;
      })
      .addCase(fetchMyReferrals.fulfilled, (state, action) => {
        state.referralsLoading = false;
        const raw: any[] = Array.isArray(action.payload?.referrals) ? action.payload.referrals : (Array.isArray(action.payload) ? action.payload : []);
        state.referrals = raw.map((item: any) => ({
          ...item,
          id: item.id ?? item.referral_id,
          referrer_name: item.referrer_name ?? item.referrer?.name ?? "",
          referrer_email: item.referrer_email ?? item.referrer?.email ?? "",
          referrer_department: item.referrer_department ?? item.referrer?.department ?? item.department ?? "",
          job_title: item.job_title ?? item.job?.job_title ?? "",
          referred_at: item.referred_at ?? item.submitted_at,
          bonus_amount: item.bonus_amount ?? null,
          bonus_status: item.bonus_status ?? null,
        }));
      })
      .addCase(fetchHRReferrals.rejected, (state, action) => {
        state.referralsLoading = false;
        state.error = action.payload as string;
      })

      .addCase(fetchMyReferrals.rejected, (state, action) => {
        state.referralsLoading = false;
        state.error = action.payload as string;
      })

      .addCase(fetchHRReferrals.pending, (state) => {
        state.referralsLoading = true;
      })
      .addCase(fetchHRReferrals.fulfilled, (state, action) => {
        state.referralsLoading = false;
        const raw: any[] = Array.isArray(action.payload?.referrals) ? action.payload.referrals : (Array.isArray(action.payload) ? action.payload : []);
        state.referrals = raw.map((item: any) => ({
          ...item,
          id: item.id ?? item.referral_id,
          referrer_name: item.referrer_name ?? item.referrer?.name ?? "",
          referrer_email: item.referrer_email ?? item.referrer?.email ?? "",
          referrer_department: item.referrer_department ?? item.referrer?.department ?? item.department ?? "",
          job_title: item.job_title ?? item.job?.job_title ?? "",
          referred_at: item.referred_at ?? item.submitted_at,
          bonus_amount: item.bonus_amount ?? null,
          bonus_status: item.bonus_status ?? null,
        }));
      })



      .addCase(fetchReferralStatistics.fulfilled, (state, action) => {
        state.referralStats = action.payload;
      })

      .addCase(fetchTopReferrers.fulfilled, (state, action) => {
        state.topReferrers = action.payload || [];
      })

      .addCase(fetchReferralRewards.pending, (state) => {
        state.referralRewardsLoading = true;
      })
      .addCase(fetchReferralRewards.fulfilled, (state, action) => {
        state.referralRewardsLoading = false;
        state.referralRewards = action.payload?.rewards || [];
      })
      .addCase(fetchReferralRewards.rejected, (state, action) => {
        state.referralRewardsLoading = false;
        state.error = action.payload as string;
      })

      .addCase(fetchReferralRewardSummary.fulfilled, (state, action) => {
        state.referralRewardSummary = action.payload || [];
      })

      .addCase(updateReferralRewardStatus.fulfilled, (state, action: any) => {
        const updatedReward = action.payload;
        state.referralRewards = state.referralRewards.map((r) =>
          r.id === updatedReward.id ? { ...r, ...updatedReward } : r,
        );
      })

      
      .addCase(fetchCareerPageSettings.pending, (state) => {
        state.careerPageSettingsLoading = true;
      })
      .addCase(fetchCareerPageSettings.fulfilled, (state, action) => {
        state.careerPageSettingsLoading = false;
        state.careerPageSettings = action.payload || null;
      })
      .addCase(fetchCareerPageSettings.rejected, (state, action) => {
        state.careerPageSettingsLoading = false;
        state.error = action.payload as string;
      })

      
      .addCase(fetchCareerPageLink.pending, (state) => {
        state.careerPageLinkLoading = true;
      })
      .addCase(fetchCareerPageLink.fulfilled, (state, action) => {
        state.careerPageLinkLoading = false;
        state.careerPageLink = action.payload || null;
      })
      .addCase(fetchCareerPageLink.rejected, (state, action) => {
        state.careerPageLinkLoading = false;
        state.error = action.payload as string;
      })

      .addCase(createCareerPageLinkThunk.fulfilled, (state, action) => {
        state.careerPageLink = action.payload || null;
      })

      .addCase(toggleCareerPageLinkThunk.fulfilled, (state, action) => {
        state.careerPageLink = action.payload;
        state.careerPageLinkLoading = false;
      })
      .addCase(toggleCareerPageLinkThunk.pending, (state) => {
        state.careerPageLinkLoading = true;
      })
      .addCase(toggleCareerPageLinkThunk.rejected, (state) => {
        state.careerPageLinkLoading = false;
      })

      .addCase(fetchIntegrations.pending, (state) => {
        state.integrationsLoading = true;
      })
      .addCase(fetchIntegrations.fulfilled, (state, action) => {
        state.integrationsLoading = false;
        state.integrations = action.payload || [];
      })
      .addCase(fetchIntegrations.rejected, (state, action) => {
        state.integrationsLoading = false;
        state.error = action.payload as string;
      })

      
      .addCase(fetchATSScoringWeights.pending, (state) => {
        state.atsScoringWeightsLoading = true;
      })
      .addCase(fetchATSScoringWeights.fulfilled, (state, action) => {
        state.atsScoringWeightsLoading = false;
        state.atsScoringWeights = action.payload || null;
      })
      .addCase(fetchATSScoringWeights.rejected, (state, action) => {
        state.atsScoringWeightsLoading = false;
        state.error = action.payload as string;
      })

      // ── Application Rounds (Pipeline) ──
      .addCase(fetchApplicationRounds.pending, (state) => {
        state.applicationRoundsLoading = true;
      })
      .addCase(fetchApplicationRounds.fulfilled, (state, action) => {
        state.applicationRoundsLoading = false;
        state.applicationRounds = action.payload?.rounds || [];
      })
      .addCase(fetchApplicationRounds.rejected, (state, action) => {
        state.applicationRoundsLoading = false;
        state.error = action.payload as string;
      })

      .addCase(updateApplicationRoundResult.fulfilled, (state, action) => {
        const result = action.payload;
        if (result?.application_round_id) {
          state.applicationRounds = state.applicationRounds.map((r) =>
            r.id === result.application_round_id
              ? { ...r, round_status: result.round_status, round_status_text: result.round_status_text }
              : r,
          );
        }
      });
  },
});

export const {
  setActiveCompany,
  setJobFilters,
  resetJobFilters,
  setApplicationFilters,
  resetApplicationFilters,
  setActiveJob,
  setActiveApplication,
  clearError,
} = recruitmentSlice.actions;

export default recruitmentSlice.reducer;
